import React from 'react';
import {
  Paper,
  Box,
  Grid,
  Typography,
  Chip,
} from '@mui/material';
import { roundFloatToCents } from '@hydra/internal';

interface ICreditApplicationSummaryProps {
  totalAvailable: number;
  totalApplying: number;
  totalRemaining: number;
}

export const CreditApplicationSummary: React.FC<ICreditApplicationSummaryProps> = ({
  totalAvailable,
  totalApplying,
  totalRemaining,
}) => {
  // Compare the cent-rounded remainder, not the raw float: summing many invoice-application
  // amounts as IEEE 754 doubles can leave a sub-cent residue that is genuinely zero at
  // currency precision but would otherwise read as "over applied".
  const isOverAvailable = roundFloatToCents(totalRemaining) < 0;

  return (
    <Paper sx={{ p: 2, bgcolor: '#f8f9fa', border: '1px solid #dee2e6', mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
        Application Summary
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Total Available Credit
            </Typography>
            <Chip
              label={`$${roundFloatToCents(totalAvailable)}`}
              color="success"
              size="medium"
              sx={{ fontSize: '1rem', height: 'auto', py: 1 }}
            />
          </Box>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Total Being Applied
            </Typography>
            <Chip
              label={`$${roundFloatToCents(totalApplying)}`}
              color="primary"
              variant="outlined"
              size="medium"
              sx={{ fontSize: '1rem', height: 'auto', py: 1 }}
            />
          </Box>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {isOverAvailable ? 'Over Applied' : 'Remaining Available'}
            </Typography>
            <Chip
              label={`$${roundFloatToCents(Math.abs(totalRemaining))}`}
              color={isOverAvailable ? 'error' : 'default'}
              size="medium"
              sx={{ fontSize: '1rem', height: 'auto', py: 1 }}
            />
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};
